import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type EntityScope = 'contact' | 'job' | 'invoice' | 'document' | 'time_entry';
export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'email'
  | 'phone'
  | 'select'
  | 'multi_select'
  | 'checkbox'
  | 'radio'
  | 'file'
  | 'table'
  | 'country'
  | 'postal_code'
  | 'user_select';
export type RenderAs = 'tab' | 'section' | 'accordion';
export type FieldWidth = 'half' | 'full' | 'third';
export type ConditionOperator = 'eq' | 'neq' | 'in' | 'not_in' | 'is_empty' | 'is_not_empty';

export interface FormDefinition {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  entity_scope: EntityScope;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FormFieldCondition {
  id: string;
  field_id: string;
  depends_on_field_id: string;
  operator: ConditionOperator;
  value: unknown;
  logic_group: 'AND' | 'OR';
}

export interface FormField {
  id: string;
  section_id: string;
  field_key: string;
  label: string;
  field_type: FieldType;
  is_required: boolean;
  display_order: number;
  placeholder: string | null;
  hint_text: string | null;
  default_value: string | null;
  width: FieldWidth;
  options: Array<{ value: string; label: string }>;
  validation_rules: Record<string, unknown>;
  is_system_field: boolean;
  db_column: string | null;
  lookup_category: string | null;
  is_active: boolean;
  conditions: FormFieldCondition[];
}

export interface FormSection {
  id: string;
  form_definition_id: string;
  name: string;
  description: string | null;
  display_order: number;
  render_as: RenderAs;
  is_active: boolean;
  parent_tab_id: string | null;
  fields: FormField[];
}

export interface FormDefinitionFull extends FormDefinition {
  sections: FormSection[];
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const formBuilderKeys = {
  all: ['form-builder'] as const,
  list: (scope?: string) => [...formBuilderKeys.all, 'list', scope] as const,
  detail: (id: string) => [...formBuilderKeys.all, 'detail', id] as const,
  resolve: (entityTypeId: string) => [...formBuilderKeys.all, 'resolve', entityTypeId] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useFormDefinitions(entityScope?: string) {
  return useQuery({
    queryKey: formBuilderKeys.list(entityScope),
    queryFn: async () => {
      const params = entityScope ? `?entity_scope=${entityScope}` : '';
      const res = await apiClient.get<ApiResponse<FormDefinition[]>>(`/form-builder${params}`);
      return res.data.data ?? [];
    },
  });
}

export function useFormDefinitionFull(id: string) {
  return useQuery({
    queryKey: formBuilderKeys.detail(id),
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<FormDefinitionFull>>(`/form-builder/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });
}

export function useResolveFormForEntityType(entityTypeId: string) {
  return useQuery({
    queryKey: formBuilderKeys.resolve(entityTypeId),
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<FormDefinitionFull | null>>(
        `/form-builder/resolve/${entityTypeId}`,
      );
      return res.data.data ?? null;
    },
    enabled: !!entityTypeId,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useCreateFormDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; description?: string; entity_scope: EntityScope }) => {
      const res = await apiClient.post<ApiResponse<FormDefinition>>('/form-builder', data);
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.all });
    },
  });
}

export function useUpdateFormDefinition(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name?: string;
      description?: string | null;
      is_active?: boolean;
    }) => {
      const res = await apiClient.put<ApiResponse<FormDefinition>>(`/form-builder/${id}`, data);
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.all });
      qc.invalidateQueries({ queryKey: formBuilderKeys.detail(id) });
    },
  });
}

export function useDeleteFormDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/form-builder/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.all });
    },
  });
}

export function useDuplicateFormDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const res = await apiClient.post<ApiResponse<FormDefinition>>(
        `/form-builder/${id}/duplicate`,
        { name },
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.all });
    },
  });
}

export function useCreateSection(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      description?: string;
      display_order?: number;
      render_as?: RenderAs;
    }) => {
      const res = await apiClient.post<ApiResponse<FormSection>>(
        `/form-builder/${formId}/sections`,
        data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.detail(formId) });
    },
  });
}

export function useUpdateSection(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sectionId,
      data,
    }: {
      sectionId: string;
      data: Record<string, unknown>;
    }) => {
      const res = await apiClient.put<ApiResponse<FormSection>>(
        `/form-builder/${formId}/sections/${sectionId}`,
        data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.detail(formId) });
    },
  });
}

export function useDeleteSection(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sectionId: string) => {
      await apiClient.delete(`/form-builder/${formId}/sections/${sectionId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.detail(formId) });
    },
  });
}

export function useCreateField(formId: string, sectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post<ApiResponse<FormField>>(
        `/form-builder/${formId}/sections/${sectionId}/fields`,
        data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.detail(formId) });
    },
  });
}

export function useUpdateField(formId: string, sectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ fieldId, data }: { fieldId: string; data: Record<string, unknown> }) => {
      const res = await apiClient.put<ApiResponse<FormField>>(
        `/form-builder/${formId}/sections/${sectionId}/fields/${fieldId}`,
        data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.detail(formId) });
    },
  });
}

export function useDeleteField(formId: string, sectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fieldId: string) => {
      await apiClient.delete(`/form-builder/${formId}/sections/${sectionId}/fields/${fieldId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.detail(formId) });
    },
  });
}

export function useCreateCondition(formId: string, sectionId: string, fieldId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      depends_on_field_id: string;
      operator: string;
      value: unknown;
      logic_group?: string;
    }) => {
      const res = await apiClient.post<ApiResponse<FormFieldCondition>>(
        `/form-builder/${formId}/sections/${sectionId}/fields/${fieldId}/conditions`,
        data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.detail(formId) });
    },
  });
}

export function useDeleteCondition(formId: string, sectionId: string, fieldId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conditionId: string) => {
      await apiClient.delete(
        `/form-builder/${formId}/sections/${sectionId}/fields/${fieldId}/conditions/${conditionId}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formBuilderKeys.detail(formId) });
    },
  });
}

// ─── Field Library ──────────────────────────────────────────────────────────

export interface FieldLibraryItem {
  id: string;
  tenant_id: string;
  field_key: string;
  label: string;
  description: string | null;
  field_type: FieldType;
  is_system_field: boolean;
  db_column: string | null;
  lookup_category: string | null;
  options: Array<{ value: string; label: string }>;
  validation_rules: Record<string, unknown>;
  default_value: string | null;
  placeholder: string | null;
  hint_text: string | null;
  width: FieldWidth;
  is_active: boolean;
}

export function useFieldLibrary() {
  return useQuery({
    queryKey: [...formBuilderKeys.all, 'field-library'],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<FieldLibraryItem[]>>(
        '/form-builder/field-library?is_active=true',
      );
      return res.data.data ?? [];
    },
    staleTime: 0, // Always fetch fresh when modal opens
  });
}

export function useCreateFieldLibraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post<ApiResponse<FieldLibraryItem>>(
        '/form-builder/field-library',
        data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...formBuilderKeys.all, 'field-library'] });
    },
  });
}

export function useUpdateFieldLibraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await apiClient.put<ApiResponse<FieldLibraryItem>>(
        `/form-builder/field-library/${id}`,
        data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...formBuilderKeys.all, 'field-library'] });
    },
  });
}

export function useDeleteFieldLibraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/form-builder/field-library/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...formBuilderKeys.all, 'field-library'] });
    },
  });
}
